import {
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  MobileStepper,
  Box,
  IconButton,
} from '@mui/material';
import { Close, HelpOutline } from '@mui/icons-material';
import { DocusealForm } from '@docuseal/react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import formsService from '../../api/services/forms';
import { listings, offers, OfferStatus } from '@prisma/client';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { CircularProgress } from '@mui/material';
import BuyerOfferSuccess from '../OfferModal/components/BuyerOfferSuccess';
import useIsMobile from '../../hooks/useIsMobile';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import offersService from '../../api/services/offers';
import { useAppDispatch } from '../../store/store';
import { fetchOffers } from '../../store/slices/offers';
import { useSnackbar } from 'notistack';

interface Props {
  offer: offers;
  listing: listings;
}

const SignBuyerOfferModal = NiceModal.create<Props>((props) => {
  const PURCHASE_AGREEMENT = 6;
  const LEAD_FORM_ID = 8;
  const BUYER_FORM_ID = 7;
  const BUYER_CLOSING_COST = 9;
  const TN_BUYER_AGREEMENT = 12;
  const TN_AGENCY_DISCLOSURE = 11;
  const GA_VA_LOAN_EXHIBIT = 21;
  const GA_EARNEST_MONEY_EXHIBIT = 20;

  const { offer, listing } = props;
  const isTennesseeProperty =
    listing.state === 'TN' || listing.state === 'Tennessee';
  const isVaLoan = offer.loanType === 'VA';
  const isGeorgiaProperty = listing.state === 'GA';
  const isAlabamaProperty = listing.state === 'AL';
  const isMobile = useIsMobile();
  const [, setLoading] = useState(false);
  const [slug, setSlug] = useState('');
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const modal = useModal();
  const [activeStep, setActiveStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const showLeadForm = listing.yearBuilt < 1978;

  // Add educational content for each step
  const STEP_EDUCATION = {
    PURCHASE_AGREEMENT: {
      title: 'About Purchase Agreements',
      content:
        // eslint-disable-next-line max-len
        'A purchase agreement is a legally binding contract between you and the seller. It outlines the terms and conditions of the sale, including price, closing date, contingencies, and other key details. Review carefully before signing.',
    },
    CLOSING_COSTS: {
      title: 'Understanding Closing Costs',
      content:
        // eslint-disable-next-line max-len
        "Closing costs are additional expenses beyond the purchase price. These typically include lender fees, title insurance, property taxes, and other settlement charges. It's important to understand these costs as they affect your total investment.",
    },
    BUYER_AGREEMENT: {
      title: 'Buyer Agreement Explained',
      content:
        // eslint-disable-next-line max-len
        'The buyer agreement establishes the relationship between you and your real estate representative. It covers their responsibilities, compensation, and the terms of their service in helping you purchase the property.',
    },
    LEAD_PAINT: {
      title: 'Lead Paint Disclosure',
      content:
        // eslint-disable-next-line max-len
        'For homes built before 1978, federal law requires sellers to disclose known lead-based paint hazards. This document informs you about potential risks and your rights regarding lead paint testing and assessment.',
    },
    TN_AGENCY_DISCLOSURE: {
      title: 'Tennessee Agency Disclosure',
      content:
        // eslint-disable-next-line max-len
        'The Tennessee agency disclosure is a document that informs you about the agency relationship between you and your real estate representative. It covers their responsibilities, compensation, and the terms of their service in helping you purchase the property.',
    },
  };

  const [offerDocs, setOfferDocs] = useState<Array<{ name: string; buyerSlug: string }>>([]);
  const hasHoaAddendum = offerDocs.some((d) => d.name.includes('Community Association'));

  const steps = [
    {
      label: 'Closing Costs',
      formId: BUYER_CLOSING_COST,
      isSigned: false,
      getForm: () => createForm(BUYER_CLOSING_COST),
      completed: false,
      description: 'Review and sign the buyer closing costs document',
      education: STEP_EDUCATION.CLOSING_COSTS,
    },
    ...(isTennesseeProperty
      ? [
          {
            label: 'Tennessee Agency Disclosure',
            formId: TN_AGENCY_DISCLOSURE,
            isSigned: false,
            getForm: () => createForm(TN_AGENCY_DISCLOSURE),
          },
        ]
      : []),
    {
      label: isTennesseeProperty
        ? 'Tennessee Buyer Agreement'
        : 'Buyer Agreement',
      formId: isTennesseeProperty ? TN_BUYER_AGREEMENT : BUYER_FORM_ID,
      isSigned: false,
      getForm: () =>
        createForm(isTennesseeProperty ? TN_BUYER_AGREEMENT : BUYER_FORM_ID),
      completed: false,
      description: isTennesseeProperty
        ? 'Sign the Tennessee buyer agreement terms'
        : 'Sign the buyer agreement terms',
      education: STEP_EDUCATION.BUYER_AGREEMENT,
    },
    {
      label: 'Purchase Agreement',
      formId: PURCHASE_AGREEMENT,
      isSigned: false,
      getForm: () => getForm(offer.id),
      description: 'Sign the official purchase agreement for the property',
      education: STEP_EDUCATION.PURCHASE_AGREEMENT,
    },
    // Driven by what was actually generated rather than by state alone: the
    // addendum only exists when the AR form was drawn for an HOA property, and it
    // carries a required buyer signature that would otherwise never be presented.
    ...(isAlabamaProperty && hasHoaAddendum
      ? [
          {
            label: 'Community Association Addendum',
            formId: PURCHASE_AGREEMENT,
            isSigned: false,
            getForm: () => getExhibitSlug('Community Association'),
            completed: false,
            description: 'Sign the community association addendum to the purchase agreement',
          },
        ]
      : []),
    ...(isGeorgiaProperty
      ? [
          {
            label: 'Earnest Money Exhibit',
            formId: GA_EARNEST_MONEY_EXHIBIT,
            isSigned: false,
            getForm: () => getExhibitSlug('Earnest Money'),
            completed: false,
            description: 'Closing attorney earnest money holder exhibit',
          },
        ]
      : []),
    ...(isGeorgiaProperty && isVaLoan
      ? [
          {
            label: 'VA Loan Contingency Exhibit',
            formId: GA_VA_LOAN_EXHIBIT,
            isSigned: false,
            getForm: () => getExhibitSlug('VA Loan'),
            completed: false,
            description: 'VA loan contingency terms and conditions',
          },
        ]
      : []),
    ...(showLeadForm
      ? [
          {
            label: 'Lead Paint Disclosure',
            formId: LEAD_FORM_ID,
            isSigned: false,
            getForm: () => createForm(LEAD_FORM_ID),
            completed: false,
            description: 'Required disclosure for properties built before 1978',
            education: STEP_EDUCATION.LEAD_PAINT,
          },
        ]
      : []),
  ];

  const isLastStep = activeStep === steps.length - 1;

  useEffect(() => {
    steps[activeStep].getForm();
  }, [activeStep]);

  const handleNext = () => {
    setSlug(''); // Clear the current slug before loading new form
    setActiveStep((prevStep) => prevStep + 1);
  };

  const getForm = async (offerId: string) => {
    try {
      const res = await formsService.get(offerId);
      if (res.offerDocuments) {
        setOfferDocs(res.offerDocuments.filter((d: any) => d.buyerSlug));
      }
      if (res.completed) {
        handleNext();
      } else {
        setSlug(res.buyerSlug);
      }
    } catch (error) {
      enqueueSnackbar('Please generate documents first before signing', {
        variant: 'error',
      });
    }
  };

  const getExhibitSlug = async (docName: string) => {
    let docs = offerDocs;
    if (docs.length === 0) {
      try {
        const res = await formsService.get(offer.id);
        docs = (res.offerDocuments || []).filter((d: any) => d.buyerSlug);
        setOfferDocs(docs);
      } catch {
        return;
      }
    }
    const doc = docs.find((d) => d.name.includes(docName));
    if (doc?.buyerSlug) {
      setSlug(doc.buyerSlug);
    }
  };

  const createForm = async (formId: number) => {
    setLoading(true);
    const res = await formsService.post({
      listingId: offer.listingId,
      formId,
      offerId: offer.id,
    });
    setLoading(false);
    setSlug(res);
  };

  return (
    <Dialog
      open={modal.visible}
      onClose={modal.remove}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Typography variant="h6" fontWeight={600}>
            Review Offer Documents
          </Typography>
          <IconButton onClick={() => modal.remove()}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          {/* Progress indicator */}
          {!isComplete && (
            <Box>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0 }}
              >
                <MobileStepper
                  variant="progress"
                  steps={steps.length}
                  position="static"
                  activeStep={activeStep}
                  sx={{
                    flexGrow: 1,
                    mb: 0,
                    background: 'transparent',
                    '& .MuiLinearProgress-root': {
                      width: '100%',
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: (theme) => theme.palette.grey[200],
                    },
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                    },
                  }}
                  nextButton={null}
                  backButton={null}
                />
                <Typography variant="body2" color="text.secondary">
                  Step {activeStep + 1} of {steps.length}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Current step info */}
          {!isComplete && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" gutterBottom>
                  {steps[activeStep].label}
                </Typography>
                <CustomTooltip
                  title={
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {steps[activeStep].education?.title}
                      </Typography>
                      <Typography variant="body2">
                        {steps[activeStep].education?.content}
                      </Typography>
                    </Box>
                  }
                  arrow
                  width={'300px'}
                >
                  <IconButton size="small">
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </CustomTooltip>
              </Stack>
              <Typography variant="body2" color="text.primary" gutterBottom>
                {steps[activeStep].description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If you have a second buyer on your offer, they will receive an
                email to sign the documents after you sign.
              </Typography>
            </Box>
          )}

          {/* Document form */}
          {isComplete ? (
            <BuyerOfferSuccess finalReview={true} />
          ) : (
            <Box sx={{ minHeight: '60vh' }}>
              {slug !== '' ? (
                <DocusealForm
                  key={slug}
                  src={`https://docuseal.co/s/${slug}`}
                  email={currentUser?.email ?? ''}
                  onComplete={async (data) => {
                    if (!isLastStep) {
                      handleNext();
                    } else {
                      setIsComplete(true);
                      await offersService.patch(offer.id, {
                        status: OfferStatus.SIGNED,
                      });
                      dispatch(fetchOffers());
                    }
                  }}
                  expand={false}
                  withFieldNames={false}
                  withTitle={false}
                  signature={`${currentUser?.firstName} ${currentUser?.lastName}`}
                  rememberSignature={true}
                  goToLast={false}
                  role="Buyer"
                />
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <CircularProgress />
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {!isComplete && <Button onClick={modal.remove}>Cancel</Button>}
      </DialogActions>
    </Dialog>
  );
});

export default SignBuyerOfferModal;
